import { useMemo } from 'react';

import type { AggregatedIssue, Issue } from '@/entities/issue';
import type { Signal } from '@/entities/signal';
import { filterMap } from '@/utils/lazy';

import { calculatePeriodCounts } from '../lib/signal-date-preset.utils';
import { changeSort, feedbackCountSort } from '../lib/signal-issue.utils';
import type {
  CompositedSignalIssue,
  SIGNAL_SEVERITY,
  SignalDateSelectionInput,
} from '../model';

interface UseComputedIssuesParams {
  issueAggregates: AggregatedIssue[] | undefined;
  originIssues: Issue[];
  signals: Signal[];
  datePreset: SignalDateSelectionInput;
  sortStatus: NotNullableSortStatusT;
}

const defaultSorter =
  (sortStatus: NotNullableSortStatusT) =>
  (
    a: Omit<CompositedSignalIssue, 'issueIndex'>,
    b: Omit<CompositedSignalIssue, 'issueIndex'>,
  ) => {
    let result = 0;
    switch (sortStatus.sortBy) {
      case 'feedback':
        result = feedbackCountSort(a, b);
        break;
      case 'changes':
        result = changeSort(a, b);
        break;
      default:
        result = feedbackCountSort(a, b);
    }
    return sortStatus.orderStatus === 'asc' ? -result : result;
  };

export const useComputedIssues = ({
  issueAggregates,
  originIssues,
  signals,
  datePreset,
  sortStatus,
}: UseComputedIssuesParams): CompositedSignalIssue[] => {
  return useMemo(() => {
    // signal id → name 매핑
    const signalMap = new Map(signals.map((s) => [s.id, s.title]));

    return filterMap<
      AggregatedIssue,
      Omit<CompositedSignalIssue, 'issueIndex'>
    >(issueAggregates || [], (issue) => {
      const originalIssue = originIssues.find((i) => i.name === issue.name);
      if (!originalIssue) return null;
      if (originalIssue.hidden) return null;

      const { selectedPeriodCount, comparisonPeriodCount } =
        calculatePeriodCounts(issue.values, datePreset);

      // 선택 기간에 Feedback 데이터가 없으면 제외
      if (selectedPeriodCount === 0) return null;

      // signal_ids를 signal 이름으로 변환
      const issueSignalIds = originalIssue.signal_ids ?? [];
      const signalTitles = issueSignalIds
        .map((id) => signalMap.get(id))
        .filter((name): name is string => name != null);

      return {
        name: issue.name,
        severity: originalIssue.severity as SIGNAL_SEVERITY,
        favorite: originalIssue.favorite,
        reason: originalIssue.reason,
        detectedAt: originalIssue.detected_at,
        issueDateChunks: issue.values,
        selectedPeriodCount,
        comparisonPeriodCount,
        signalIds: issueSignalIds,
        signalTitles,
      };
    })
      .sort(defaultSorter(sortStatus))
      .map((issue, index) => ({
        ...issue,
        issueIndex: index + 1,
      }));
  }, [datePreset, issueAggregates, originIssues, signals, sortStatus]);
};
